import {Component, OnInit} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {
  ImeListService,
  IntegratedMasterFilterModel,
  IntegratedMasterListModel
} from './ime-list.service';
import {map, takeUntil} from 'rxjs/operators';
import {
  ClassificatorValuesQuery,
  FilterModel,
  GLOBAL,
  LeModalService, TableComponent
} from 'common-shared';
import {StateService} from 'web-shared';
import {ProcessesQuery} from '../processes/process.store';
import {ItemsQuery} from 'web-shared';
import {ManufacturingItemEditModalComponent} from '../manufacturing-item/manufacturing-item-edit-modal/manufacturing-item-edit-modal.component';
import {SpecLineFilterModel} from 'web-shared';
import {ManufacturingItemGroupEditModalComponent} from "../manufactured-item-groups/manufacturing-item-group-edit-modal/manufacturing-item-group-edit-modal.component";
import {ItemQuery} from "web-shared";
import {ItemGroupQuery} from "web-shared";
import {ItemGroupsQuery} from "web-shared";
import {SortMeta} from "primeng/api";
import {IntegratedMasterListColumns, TableColumn} from "../../core/table-column";
import {ResourceQuery} from "../resources/models/resource.query";
import {ResourcesQuery} from "../resources/resource.store";
import {ResourceService} from "../resources/resource.service";

@Component({
  selector: 'app-ime-list',
  templateUrl: './ime-list.component.html',
  styles: []
})
export class ImeListComponent extends TableComponent implements OnInit {
  filter$: BehaviorSubject<FilterModel<IntegratedMasterFilterModel>>;
  filteredData$: BehaviorSubject<IntegratedMasterListModel[]> = new BehaviorSubject<IntegratedMasterListModel[]>([]);
  instructionTypes: { Code: string, Name: string }[] = [{Code: 'I', Name: 'I - Input'}, {Code: 'U', Name: 'U - Use'}, {Code: 'O', Name: 'O - Output'}];
  targetTypes: { Code: string, Name: string }[] = [{Code: 'Item', Name: 'Item'}, {Code: 'ItemGroup', Name: 'Item Template'}, {Code: 'Configuration', Name: 'Configuration'}];
  allColumns: TableColumn<IntegratedMasterListColumns>[] = [];
  columnIdentifiers = IntegratedMasterListColumns;
  itemsWithEmpty$: Observable<ItemQuery[]>;
  itemGroupsWithEmpty$: Observable<ItemGroupQuery[]>;
  resourcesWithEmpty$: Observable<ResourceQuery[]>;

  constructor(private state: StateService, private imeListService: ImeListService, private modalService: LeModalService, public processesQuery: ProcessesQuery, public itemsQuery: ItemsQuery, public resourcesQuery: ResourcesQuery, public classificatorValuesQuery: ClassificatorValuesQuery, private resourceService: ResourceService, public itemGroupsQuery: ItemGroupsQuery) {
    super();
  }

  ngOnInit(): void {
    this.restoreFilterState();
    this.configureFilter();

    this.itemsWithEmpty$ = combineLatest([of([new ItemQuery({Id: null, Code: '-'})]),
      this.itemsQuery.getAll$]).pipe(map((value: [ItemQuery[], ItemQuery[]]) => {
      return [...value[0], ...value[1]];
    }));
    this.itemGroupsWithEmpty$ = combineLatest([of(new ItemGroupQuery({Id: null, Code: '-'})),
      this.itemGroupsQuery.getAll$]).pipe(map((value: [ItemGroupQuery, ItemGroupQuery[]]) => {
      return [value[0], ...value[1]];
    }));
    this.resourcesWithEmpty$ = combineLatest([of(new ResourceQuery({Id: null, Code: '-'})),
      this.resourcesQuery.getAll$]).pipe(map((value: [ResourceQuery, ResourceQuery[]]) => {
      return [value[0], ...value[1]];
    }));
  }

  private restoreFilterState() {
    const filterState = this.state.getFilterState<IntegratedMasterFilterModel>() || new FilterModel<IntegratedMasterFilterModel>({Data: new IntegratedMasterFilterModel(), AdvancedFilter: true});
    this.imeListService.getAllColumns().forEach(column => {
      column.IsSelected = filterState.Data.SelectedColumns.includes(column.Identifier);
      this.allColumns.push(column);
    });

    this.filter$ = new BehaviorSubject<FilterModel<IntegratedMasterFilterModel>>(filterState);
  }

  private configureFilter() {
    this.filter$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.state.setFilterState(value);
    });
  }

  load() {
    let filter = this.filter$.getValue();
    filter.Page = this.page;
    filter.Data.SelectedColumns = this.allColumns.filter(field => field.IsSelected).map(field => field.Identifier);
    this.imeListService.getByFilter(filter).pipe(takeUntil(this.componentDestroyed)).subscribe(pagedData => {
      this.filteredData$.next(pagedData.Data);
      this.page = pagedData.Page;
    });
  }

  onSort(event: SortMeta) {
    const filter = this.filter$.getValue();
    filter.Sort = [event];
    this.page.Number = 0;
    this.filter$.next(filter);
    this.load();
  }

  onPage(event: any) {
    this.page.Number = event.first / event.rows;
    this.page.Size = event.rows;
    const filter = this.filter$.getValue();
    filter.Page = this.page;
    this.state.setFilterState(filter);
    this.load();
  }

  open(row: IntegratedMasterListModel) {
    if (row.ItemId) {
      this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityId: row.ItemId} as ManufacturingItemEditModalComponent, class: 'modal-xl'});
    } else if (row.ItemGroupId) {
      this.modalService.show(ManufacturingItemGroupEditModalComponent, {initialState: {entityId: row.ItemGroupId} as ManufacturingItemGroupEditModalComponent, class: 'modal-xl'});
    }
  }

  addSpec(filter: FilterModel<IntegratedMasterFilterModel>, model: string) {
    filter.Data[model] = [...filter.Data[model], new SpecLineFilterModel()];
  }

  toggleColumn(field: TableColumn<IntegratedMasterListColumns>) {
    field.IsSelected = !field.IsSelected;
    const filter = this.filter$.getValue();
    filter.Data.SelectedColumns = this.allColumns.filter(column => column.IsSelected).map(column => column.Identifier);
    this.state.setFilterState(filter);
    this.allColumns = [...this.allColumns];
  }

  toggleAllColumns() {
    const filter = this.filter$.getValue();
    if (this.allColumns.length === this.allColumns.filter(column => column.IsSelected).length) {
      this.allColumns.forEach(column => column.IsSelected = false);
      filter.Data.SelectedColumns = [];
    } else {
      this.allColumns.forEach(column => column.IsSelected = true);
      filter.Data.SelectedColumns = this.allColumns.map(column => column.Identifier);
    }
    this.state.setFilterState(filter);
    this.allColumns = [...this.allColumns];
  }

  updateFilter(model: string, values: string[]) {
    let filter = this.filter$.getValue();
    filter.Data[model] = values;
    this.filter$.next(filter);
  }

  saveFilter(filter: FilterModel<IntegratedMasterFilterModel>) {
    this.resourceService.addCurrentUserListPropertyLineValue(GLOBAL.Properties.ImeListFilterSettings, JSON.stringify(filter)).pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      let currentUser = this.state.currentUser$.value;
      this.resourceService.getCurrentUserSpecs().pipe(takeUntil(this.componentDestroyed)).subscribe(specs => {
        currentUser.Specs = specs;
        this.state.currentUser$.next(currentUser);
      });
    });
  }
}
